






























































































































































import { addBasePath } from '~/helpers/addBasePath';
import {
  computed, defineComponent, onMounted, useFetch, useMeta, useContext, watch,
} from '@nuxtjs/composition-api';
import { CacheTagPrefix, useCache } from '@vue-storefront/cache';
import type { CacheTag } from '@vue-storefront/cache';
import {
  useUiHelpers, useUiState, useImage,
} from '~/composables';

import { useAddToCart } from '~/helpers/cart/addToCart';
import { useWishlist } from '~/modules/wishlist/composables/useWishlist';
import { useTraverseCategory } from '~/modules/catalog/category/helpers/useTraverseCategory';
import {
  getMetaInfo, getUrlMeta, getPaginationMeta, getMagentoMeta,
} from '~/helpers/getMetaInfo';
import type { Product } from '~/modules/catalog/product/types';
import LazyHydrate from 'vue-lazy-hydration';
import { getCategoryImage } from '~/helpers/getCategoryImage';
import { useUser } from '~/modules/customer/composables/useUser';
import { useCatalogProducts } from '~/modules/catalog/category/composables/useCatalogProducts';
import { useCategoryNavigation } from '~/modules/catalog/category/composables/useCategoryNavigation';
import { useCategoryContentUI } from '~/modules/catalog/category/composables/useCategoryContentUI';
import { useMagentoMetaData } from '~/composables/useMagentoMetaData';

export default defineComponent({
  name: 'CategoryPage',
  components: {
    LazyHydrate,
    CategoryPagination: () => import('~/modules/catalog/category/components/pagination/CategoryPagination.vue'),
    CategoryEmptyResults: () => import('~/modules/catalog/category/components/CategoryEmptyResults.vue'),
    CategoryFilters: () => import('~/modules/catalog/category/components/filters/CategoryFilters.vue'),
    CmsContent: () => import('~/modules/catalog/category/components/cms/CmsContent.vue'),
    CategoryProductGrid: () => import('~/modules/catalog/category/components/views/CategoryProductGrid.vue'),
    CategoryProductList: () => import('~/modules/catalog/category/components/views/CategoryProductList.vue'),
    CategoryNavbar: () => import('~/modules/catalog/category/components/navbar/CategoryNavbar.vue'),
    CategoryBreadcrumbs: () => import('~/modules/catalog/category/components/breadcrumbs/CategoryBreadcrumbs.vue'),
    SkeletonLoader: () => import('~/components/SkeletonLoader/index.vue'),
    ImageWrapper: () => import('~/components/image/ImageWrapper.vue'),
    Brands: () => import('~/components/Brands/Brands.vue'),
    CategoryTileGrid: () => import('~/modules/catalog/category/components/views/CategoryTileGrid.vue'),
  },
  transition: 'fade',
  props: {
    routeData: {
      type: Object,
      default: null,
    },
  },
  setup({ routeData }) {
    const { alternate, xDefault, load: loadMagentoMeta } = useMagentoMetaData();
    const { addTags } = useCache();
    const uiHelpers = useUiHelpers();
    const { isAuthenticated } = useUser();
    const { env, i18n } = useContext();
    const { load: loadWishlist, addOrRemoveItem } = useWishlist();
    const { addItemToCart } = useAddToCart();
    const { imageSizes, baseImageConfig, getMagentoImage } = useImage();
    const {
      activeCategory, activeMainCategory, loadCategoryTree, loadCategoryTreeLanding,
    } = useTraverseCategory();
    const {
      products,
      extendedProducts,
      filters,
      sortBy,
      pagination,
      fetchProducts,
    } = useCatalogProducts();
    const {
      fetchContent,
      categoryMeta,
      cmsContent,
      isShowCms,
      isShowProducts,
      isShowPage,
    } = useCategoryContentUI();

    const metaInfo = () => {
      const urlMeta = getUrlMeta(uiHelpers.resolvePath());
      const paginationMeta = getPaginationMeta({
        pagination: pagination.value,
        route: Number(uiHelpers.resolveQuery()?.page ?? 1),
        baseData: urlMeta,
      });
      return getMagentoMeta({
        data: categoryMeta.value,
        baseData: paginationMeta,
        alternate: alternate.value,
      });
    };
    useMeta(() => getMetaInfo({ page: { ...metaInfo(), xDefault: xDefault.value } }));

    const { productContainerElement, goToPage } = useCategoryNavigation();
    const {
      toggleFilterSidebar, toggleSortSidebar, changeToCategoryListView, changeToCategoryGridView, isFilterSidebarOpen, isSortSidebarOpen,
      isProductCatalogGridView,
    } = useUiState();

    const isLandindPage = computed(() => new Set([env.VSF_MAGENTO_BRANDS_CATEGORY_UID]).has(activeCategory.value?.uid));
    const isBrandsPage = computed(() => routeData.uid === env.VSF_MAGENTO_BRANDS_CATEGORY_UID);

    const activeCategoryName = computed(() => activeCategory.value?.name ?? '');
    const activeCategoryBanner = computed(() => activeCategory.value?.image ?? '');
    const activeCategoryMobileBanner = computed(() => activeCategory.value?.mobile_banner_image ?? '');

    const addItemToWishlist = async (product: Product) => addOrRemoveItem({ product, list: 'Category' });

    const { fetch } = useFetch(async () => {
      const categoryUid = routeData.uid;
      await loadMagentoMeta();
      await fetchContent(categoryUid);
      if (!isLandindPage.value && !isShowPage.value) {
        await fetchProducts(categoryUid, isProductCatalogGridView.value as boolean);
      }

      if (activeMainCategory.value?.uid !== env.VSF_MAGENTO_MAIN_CATEGORY_UID) {
        await (!isLandindPage.value ? loadCategoryTreeLanding(activeMainCategory.value?.uid) : loadCategoryTree(activeMainCategory.value?.uid));
      }

      if (!isAuthenticated.value) {
        const tags = [{ prefix: CacheTagPrefix.View, value: `${i18n.locale}/${categoryUid}` }];
        let productTags: CacheTag[] = [];
        if (!isLandindPage.value && !isShowPage.value) {
          const currentProducts = isProductCatalogGridView.value ? products.value : extendedProducts.value;
          productTags = currentProducts.map((product) => ({
            prefix: CacheTagPrefix.Product,
            value: `${i18n.locale}/${product.uid}`,
          }));
        }
        addTags([...tags, ...productTags]);
      }
    });
    fetch();

    onMounted(() => {
      loadWishlist();
    });

    const onReloadProducts = () => {
      fetch();
      goToPage(0);
    };

    watch(isProductCatalogGridView, () => {
      onReloadProducts();
    });

    return {
      ...uiHelpers,
      toggleFilterSidebar,
      toggleSortSidebar,
      changeToCategoryListView,
      changeToCategoryGridView,
      isFilterSidebarOpen,
      isSortSidebarOpen,
      addItemToCart,
      addItemToWishlist,
      pagination,
      products,
      extendedProducts,
      filters,
      sortBy,
      isShowCms,
      isShowProducts,
      cmsContent,
      activeCategoryName,
      activeCategoryBanner,
      activeCategoryMobileBanner,
      productContainerElement,
      categoryMeta,
      onReloadProducts,
      goToPage,
      addBasePath,
      getCategoryImage,
      imageSizes,
      baseImageConfig,
      isBrandsPage,
      getMagentoImage,
      activeCategory,
      isProductCatalogGridView,
      isShowPage,
    };
  },
  head: {},
});
